import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import type { Dispatch } from 'redux';
import { gzipSync } from 'fflate';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { UploadModal } from 'UIPackage/MainModule/components/UploadModal';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { PLAN_SIGN_IN_BROWSER_REQUIRED_FIELDS_ERROR } from 'CorePackage/MainModule/constants/notices';

import { planSignUpload } from '../actions/planActions';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';
import { getPlanData, getPlanFormRef } from '../selectors/planSelectors';

interface IPlanSignUploadModalProps {
  visible: boolean;
  onClose: () => void;
}

export const ERKNM_SIGN_DOCUMENT_WRONG_FORMAT_MESSAGE =
  'Допустимый формат .xml. Выгрузите план по кнопке «Действия» — «Экспорт плана для подписания»';
const isXmlFile = (file: File): boolean => file.name.toLowerCase().endsWith('.xml');
const GZIP_THRESHOLD = 10 * MB;

const gzipFile = async (file: File): Promise<File> => {
  const content = new Uint8Array(await file.arrayBuffer());
  const compressed = gzipSync(content);

  return new File([compressed], file.name, {
    type: 'application/gzip',
    lastModified: file.lastModified,
  });
};

export const PlanSignUploadModal = ({ visible, onClose }: IPlanSignUploadModalProps) => {
  const { id: planId } = useParams<{ id: string }>();

  const dispatch = useDispatch<Dispatch<RootActions>>();

  const isErknm = useSelector((state: IRootState) => getLawInterface(state) === LawInterfacesEnum.FZ_248);
  const documentAccept = isErknm ? '.xml' : '.xlsx';

  const plan = useSelector(getPlanData);
  const formRef = useSelector(getPlanFormRef);
  const settings = useSelector(getAdminFetchSettings);

  const isFioAndJobTitle414 = isLaterThanAdminSetting(
    plan?.created,
    settings,
    AdminSettingCodesEnum.PLAN_SIGNER_166_4_1_4_START_FROM,
  );

  const handleSave = useCallback(
    async (document: File, sign: File | null) => {
      if (!document || !sign || !planId) {
        onClose();

        return;
      }

      if (isErknm && document && !isXmlFile(document)) {
        dispatch(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SIGN_UPLOAD_PLAN,
            renderContent: ERKNM_SIGN_DOCUMENT_WRONG_FORMAT_MESSAGE,
          }),
        );

        return;
      }

      const uploadDocument = isErknm && document.size > GZIP_THRESHOLD ? await gzipFile(document) : document;

      if (!isFioAndJobTitle414) {
        dispatch(planSignUpload(+planId, uploadDocument, sign));
        onClose();

        return;
      }

      /**
       * Бэк валидирует ФИО и должность подписанта по данным из БД, но при этом
       * сохраняет загруженную подпись даже в случае ошибки валидации. Поэтому
       * проверяем и сохраняем данные подписанта до отправки подписи на бэк.
       */
      const formValues = formRef?.current.values;

      if (!formRef || formRef.current.form.getState()?.hasValidationErrors) {
        formRef?.current.form.submit();
        dispatch(
          addNoticeAction({
            renderContent: PLAN_SIGN_IN_BROWSER_REQUIRED_FIELDS_ERROR,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
        onClose();

        return;
      }

      dispatch(
        planSignUpload(+planId, uploadDocument, sign, {
          signerFullName: formValues?.signerFullName,
          signerPosition: formValues?.signerPosition?.value,
        }),
      );
      onClose();
    },
    [dispatch, onClose, planId, isErknm, isFioAndJobTitle414, formRef],
  );

  return (
    <UploadModal
      accept={documentAccept}
      validateDocument={isErknm ? isXmlFile : undefined}
      fileFormatErrorText={isErknm ? ERKNM_SIGN_DOCUMENT_WRONG_FORMAT_MESSAGE : undefined}
      modalTitle="Загрузка подписи в план"
      onClose={onClose}
      visible={visible}
      requireSign
      onSave={handleSave}
      // ↓ comment from e826b32 Anton Folumenov (September 28th, 2023)
      maxFileSize={150 * MB} // TODO: вынести в конфиг, вернуть значение "10" вместо "40"
      clearOnClose={true}
    />
  );
};
